import React, { useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// 22 questions across 7 sections, matching Sport X feedback (1).docx
// The word "game" from the doc has been replaced with "Sport X" / "the app"
// per user direction.
const SECTIONS = [
  {
    title: 'General Experience',
    questions: [
      { key: 'overall_rating', label: 'How would you rate your overall experience with Sport X?', type: 'scale5', required: true },
      { key: 'enjoyed_most', label: 'What did you enjoy the most about the app?', type: 'text' },
      { key: 'enjoyed_least', label: 'What did you enjoy the least?', type: 'text' },
    ],
  },
  {
    title: 'Gameplay & Mechanics',
    questions: [
      { key: 'controls_easy', label: 'Were the app controls easy to learn and use?', type: 'yesno' },
      { key: 'gameplay_intuitive', label: 'Did the app feel intuitive and engaging?', type: 'yesno' },
      { key: 'confusing_mechanics', label: 'Were there any mechanics that felt confusing or unnecessary?', type: 'text' },
      { key: 'difficulty', label: 'Did the difficulty feel appropriate?', type: 'choice', options: ['Too easy', 'Just right', 'Too hard'] },
    ],
  },
  {
    title: 'Bugs & Performance',
    questions: [
      { key: 'encountered_bugs', label: 'Did you encounter any bugs, glitches, crashes, or freezes?', type: 'yesno' },
      { key: 'bug_description', label: 'If yes, please describe them.', type: 'text' },
      { key: 'performance_rating', label: "How would you rate the app's performance (loading times, lag)?", type: 'scale5' },
    ],
  },
  {
    title: 'Visuals & Audio',
    questions: [
      { key: 'graphics_rating', label: 'How appealing were the graphics and animations?', type: 'scale5' },
      { key: 'sound_rating', label: 'How would you rate the sound effects and music?', type: 'scale5' },
      { key: 'visuals_audio_distracting', label: 'Did anything in the visuals or audio feel distracting or out of place?', type: 'text' },
    ],
  },
  {
    title: 'UI & Navigation',
    questions: [
      { key: 'navigation_easy', label: 'Was it easy to navigate the menus and understand UI elements?', type: 'yesno' },
      { key: 'unclear_ui', label: 'Was any information on the screen unclear or hard to read?', type: 'text' },
    ],
  },
  {
    title: 'Progression & Content',
    questions: [
      { key: 'enough_content', label: 'Did the app offer enough content to keep you interested?', type: 'yesno' },
      { key: 'progression_satisfying', label: 'Did progression (rounds, scoring, standings) feel satisfying?', type: 'yesno' },
      { key: 'missing_features', label: "Were there features or content you expected but didn't find?", type: 'text' },
    ],
  },
  {
    title: 'Overall Satisfaction & Suggestions',
    questions: [
      { key: 'improvements', label: 'What would you change or improve in the app?', type: 'text' },
      { key: 'future_features', label: 'What features would you like to see added in future updates?', type: 'text' },
      { key: 'would_recommend', label: 'Would you recommend Sport X to others?', type: 'yesno', required: true },
      { key: 'recommend_reason', label: 'Why or why not?', type: 'text' },
    ],
  },
];

const CYAN = '#06B6D4';
const BG = '#0F172A';
const CARD_BG = '#1E293B';
const BORDER = 'rgba(255, 255, 255, 0.1)';

function ScaleInput({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testIdPrefix}-${n}`}
            className="w-11 h-11 rounded-lg font-bold text-sm transition-all"
            style={{
              background: active ? CYAN : 'rgba(255,255,255,0.05)',
              border: `1px solid ${active ? CYAN : BORDER}`,
              color: active ? '#0F172A' : 'white',
            }}
          >
            {n}
          </button>
        );
      })}
      <div className="flex items-center text-xs text-slate-500 ml-2 gap-3">
        <span>1 = Poor</span>
        <span>5 = Excellent</span>
      </div>
    </div>
  );
}

function YesNoInput({ value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-2">
      {['Yes', 'No'].map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            data-testid={`${testIdPrefix}-${opt.toLowerCase()}`}
            className="px-6 py-2.5 rounded-lg font-semibold text-sm transition-all"
            style={{
              background: active ? CYAN : 'rgba(255,255,255,0.05)',
              border: `1px solid ${active ? CYAN : BORDER}`,
              color: active ? '#0F172A' : 'white',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function ChoiceInput({ value, onChange, options, testIdPrefix }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            className="px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
            style={{
              background: active ? CYAN : 'rgba(255,255,255,0.05)',
              border: `1px solid ${active ? CYAN : BORDER}`,
              color: active ? '#0F172A' : 'white',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ value, onChange, testId }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      rows={3}
      maxLength={5000}
      placeholder="Your answer…"
      className="w-full rounded-lg p-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${BORDER}`,
      }}
    />
  );
}

export default function Feedback() {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const setAnswer = (key, val) => setAnswers((prev) => ({ ...prev, [key]: val }));

  const handleSubmit = async () => {
    setError('');
    // Basic required validation
    const missing = [];
    for (const s of SECTIONS) {
      for (const q of s.questions) {
        if (q.required && (answers[q.key] === undefined || answers[q.key] === '' || answers[q.key] === null)) {
          missing.push(q.label);
        }
      }
    }
    if (missing.length) {
      setError(`Please answer the required questions before submitting (${missing.length} missing).`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback`, {
        email: email.trim() || null,
        displayName: displayName.trim() || null,
        sport: 'golf',
        answers,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: BG }}>
        <div
          className="max-w-md w-full rounded-2xl p-8 text-center"
          style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
          data-testid="feedback-success"
        >
          <div className="text-5xl mb-4">🙏</div>
          <h1 className="text-2xl font-black text-white uppercase tracking-tight mb-2">Thank you!</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Your feedback has been submitted. We appreciate you taking the time to help us make Sport X better.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20" style={{ background: BG }}>
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-4">
        <div className="mb-8">
          <div className="inline-block text-xs font-black uppercase tracking-[0.2em] mb-3" style={{ color: CYAN }}>
            Sport X Feedback
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight leading-tight mb-3">
            How did we do?
          </h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Thanks for playing The Open 2026 with us. Your feedback shapes what we build next — the more honest, the better.
          </p>
        </div>

        {error && (
          <div
            className="mb-6 rounded-lg p-4 text-sm"
            style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#FCA5A5' }}
            data-testid="feedback-error"
          >
            {error}
          </div>
        )}

        {/* Optional contact fields */}
        <div
          className="rounded-2xl p-5 mb-6 space-y-4"
          style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
        >
          <div className="text-xs font-black uppercase tracking-[0.15em] text-slate-500">
            About you (optional)
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name (optional)"
              data-testid="feedback-name-input"
              className="w-full rounded-lg p-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}` }}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com (optional)"
              data-testid="feedback-email-input"
              className="w-full rounded-lg p-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
              style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}` }}
            />
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section) => (
          <div
            key={section.title}
            className="rounded-2xl p-5 mb-6"
            style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
          >
            <h2 className="text-base font-black text-white uppercase tracking-tight mb-5" style={{ color: CYAN }}>
              {section.title}
            </h2>
            <div className="space-y-6">
              {section.questions.map((q) => (
                <div key={q.key}>
                  <label className="block text-sm text-white font-semibold mb-3 leading-snug">
                    {q.label}
                    {q.required && <span className="text-cyan-400 ml-1">*</span>}
                  </label>
                  {q.type === 'scale5' && (
                    <ScaleInput value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} testIdPrefix={`feedback-${q.key}`} />
                  )}
                  {q.type === 'yesno' && (
                    <YesNoInput value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} testIdPrefix={`feedback-${q.key}`} />
                  )}
                  {q.type === 'choice' && (
                    <ChoiceInput value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} options={q.options} testIdPrefix={`feedback-${q.key}`} />
                  )}
                  {q.type === 'text' && (
                    <TextInput value={answers[q.key]} onChange={(v) => setAnswer(q.key, v)} testId={`feedback-${q.key}-input`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="feedback-submit-button"
          className="w-full py-4 rounded-xl font-black text-sm uppercase tracking-[0.2em] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: CYAN,
            color: '#0F172A',
          }}
        >
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </button>

        <p className="text-center text-xs text-slate-500 mt-4">
          Fields marked with <span className="text-cyan-400">*</span> are required.
        </p>
      </div>
    </div>
  );
}
